import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import type { GapsResponse, ProgramCoverageResponse } from '@/shared/api/types'
import { analyticsCoverage, analyticsGaps } from '../api'
import { EnvelopeBar, Meter, Pct, Truncation, Value } from '../parts'
import { AnalyticsScreen, useAnalyticsFilters } from './shell'

/**
 * How far the portfolio reaches, by unit — and where it does not reach at all.
 *
 * ## Two means, because one of them is a trap
 *
 * `unweightedMean` averages the ward percentages. A ward of 200 people counts as
 * much as a ward of 40,000, so a portfolio that has covered every tiny ward and
 * missed every city reports a healthy average. `weightedMean` divides total
 * enrolled by total eligible. Both are on the strip; where they disagree
 * sharply, the disagreement is the finding.
 *
 * ## An unknown denominator is not a zero
 *
 * A unit whose eligible pool could not be computed is counted separately and
 * excluded from both means. Treating it as zero-eligible would inflate coverage;
 * treating it as zero-covered would deflate it. Neither is true, so it is
 * neither — it is listed under its own heading with the count.
 */
export function ProgramCoverageScreen() {
  return (
    <AnalyticsScreen
      title="Coverage & gaps"
      description="Enrolled against eligible, by unit — and the units with eligible people and nobody enrolled."
      load={(f) => analyticsCoverage(f)}
      isEmpty={(d) => (d.rows?.length ?? 0) === 0}
      exportKind="COVERAGE"
      showUnitType
    >
      {(d) => <CoverageBody d={d} />}
    </AnalyticsScreen>
  )
}

function CoverageBody({ d }: { d: ProgramCoverageResponse }) {
  const rows = [...(d.rows ?? [])].sort(
    (a, b) => Number(b.coverage ?? -1) - Number(a.coverage ?? -1),
  )
  const known = rows.filter((r) => r.eligible != null)
  // BOTH sides come from `known`. Summing enrolments over every row while the
  // denominator skips the rows with no eligible pool builds a ratio out of two
  // different populations — and prints it directly under the server's weighted
  // mean, which it then disagrees with.
  const totalEnrolled = known.reduce((n, r) => n + r.enrolledLinked, 0)
  const totalEligible = known.reduce((n, r) => n + (r.eligible ?? 0), 0)

  const weighted = d.weightedMean == null ? null : Number(d.weightedMean)
  const unweighted = d.unweightedMean == null ? null : Number(d.unweightedMean)
  const diverges = weighted != null && unweighted != null
    && Math.abs(weighted - unweighted) > 0.1

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard
          label="Coverage (weighted)"
          value={<Pct value={d.weightedMean} reason="No unit in scope has a computable eligible pool." shortReason />}
          accent
          sub={`${fmtNumber(totalEnrolled)} enrolled of ${fmtNumber(totalEligible)} eligible`}
        />
        <MetricCard
          label="Coverage (mean of units)"
          value={<Pct value={d.unweightedMean} reason="No unit in scope has a computable eligible pool." shortReason />}
          sub={(
            <span title="Every unit counts equally here, whatever its size.">
              a ward of 200 counts as much as one of 40,000
            </span>
          )}
        />
        <MetricCard label="Units in scope" value={fmtNumber(rows.length)}
          sub={`${fmtNumber(known.length)} with a known eligible pool`} />
        <MetricCard
          label="No denominator"
          value={fmtNumber(d.unknownEligibilityCount)}
          sub="Excluded from both means, not counted as zero"
        />
      </KpiStrip>

      {diverges && (
        <Note tone="bad">
          The two means differ by {((Math.abs(weighted! - unweighted!)) * 100).toFixed(0)} points.
          {weighted! < unweighted!
            ? ' The larger units are the ones being missed — the flattering figure is the one that treats every ward as equal.'
            : ' The small units are the ones being missed; the population-weighted figure is carried by a few large ones.'}
        </Note>
      )}

      <Panel title="By unit" bodyClassName="p-0">
        <ErpTable head={(
          <>
            <Th>Unit</Th>
            <Th className="text-right">Enrolled</Th>
            <Th className="text-right">Eligible</Th>
            <Th className="text-right">Coverage</Th>
            <Th>&nbsp;</Th>
          </>
        )}>
          {rows.length === 0
            ? <EmptyRow colSpan={5} message="No unit is in scope under this filter." />
            : rows.map((r) => (
              <tr key={r.unitId}>
                <Td title={r.unitPath ?? undefined}>{r.unitName}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.enrolledLinked)}</Td>
                <Td className="text-right tabular-nums">
                  <Value value={r.eligible} reason={r.unavailableReason} shortReason />
                </Td>
                <Td className="text-right"><Pct value={r.coverage} reason={r.unavailableReason} shortReason /></Td>
                <Td className="w-32">
                  {/* Driven by the SERVER's coverage. Recomputing enrolled÷eligible
                      here and clamping at 100 put two different percentages in one
                      row whenever a unit was over-covered. */}
                  <Meter value={r.coverage == null ? null : Number(r.coverage) * 100} max={100}
                    reason={r.unavailableReason} />
                </Td>
              </tr>
            ))}
        </ErpTable>
        <p className="px-4 py-2 text-erp-xs text-muted-foreground border-t border-border">
          Only enrolments linked to a voter record can be placed in a unit. An enrolment
          captured with a name and no match contributes to the programme's beneficiary
          count and to nothing on this screen.
        </p>
      </Panel>

      <GapsPanel />
    </div>
  )
}

/**
 * The screen's reason to exist: units with eligible people and zero enrolments.
 *
 * These do not appear in a coverage list sorted by percentage — they sit at the
 * bottom at 0% among the units with 1% and 2%, and a reader scanning the top of
 * the table never sees them.
 */
function GapsPanel() {
  const f = useAnalyticsFilters()
  const { data, error, loading } = useLoad<GapsResponse>(
    () => analyticsGaps(f.query), [JSON.stringify(f.query)],
  )

  if (loading && !data) return <Panel title="Gaps"><LoadingBlock label="Loading gaps…" /></Panel>
  if (error) return <Panel title="Gaps"><ErrorStrip message={error} /></Panel>
  if (!data) return null

  const gaps = data.gaps ?? []
  const unknown = data.unknownEligibility ?? []

  return (
    <div className="space-y-4">
      <Panel
        title="Nobody enrolled"
        description="Units with eligible people and not one enrolment."
        bodyClassName="p-0"
      >
        <div className="px-4 pt-3">
          <EnvelopeBar env={data} />
          <Truncation truncatedTo={data.truncatedTo} totalAvailable={data.totalAvailable}
            orderedBy={data.orderedBy} noun="units" />
        </div>
        <ErpTable head={(
          <>
            <Th>Unit</Th>
            <Th className="text-right">Eligible</Th>
          </>
        )}>
          {gaps.length === 0
            ? <EmptyRow colSpan={2} message="Every unit with eligible people has at least one enrolment." />
            : gaps.map((r) => (
              <tr key={r.unitId}>
                <Td title={r.unitPath ?? undefined}>{r.unitName}</Td>
                <Td className="text-right tabular-nums">
                  <Value value={r.eligible} reason={r.unavailableReason} shortReason />
                </Td>
              </tr>
            ))}
        </ErpTable>
      </Panel>

      {unknown.length > 0 && (
        <Panel
          title="Eligibility unknown"
          description="Not gaps. These units have no computable eligible pool, so whether they are covered cannot be said either way."
          bodyClassName="p-0"
        >
          <ErpTable head={(
            <>
              <Th>Unit</Th>
              <Th className="text-right">Enrolled</Th>
              <Th>Why</Th>
            </>
          )}>
            {unknown.map((r) => (
              <tr key={r.unitId}>
                <Td title={r.unitPath ?? undefined}>{r.unitName}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.enrolledLinked)}</Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {r.unavailableReason ?? 'No eligible pool computed.'}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}
    </div>
  )
}
